import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateAreaRequest, RoomAreaListItem, UpdateAreaRequest } from '@kreart/shared';
import { areasApi } from './api';

const ROOT = ['project-areas'] as const;
export const areasQueryKeys = {
  all: ROOT,
  list: (projectId: string, roomId: string) => [...ROOT, 'list', projectId, roomId] as const,
};

/** Areas belonging to a room. Disabled until both ids are known. */
export function useAreas(projectId: string | undefined, roomId: string | undefined) {
  return useQuery({
    queryKey: areasQueryKeys.list(projectId ?? 'nil', roomId ?? 'nil'),
    queryFn: () => areasApi.list(projectId!, roomId!),
    enabled: Boolean(projectId && roomId),
    meta: { silent: true },
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, projectId: string, roomId: string) {
  void qc.invalidateQueries({ queryKey: areasQueryKeys.list(projectId, roomId) });
}

export function useCreateArea(projectId: string, roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAreaRequest) => areasApi.create(projectId, roomId, body),
    meta: { silent: true },
    onSuccess: (created) => {
      // Seed the list cache immediately so a freshly-created area is present in
      // the picker before it is auto-selected — otherwise the combobox's
      // self-heal would drop the selection during the invalidation refetch.
      qc.setQueryData<RoomAreaListItem[]>(areasQueryKeys.list(projectId, roomId), (prev) =>
        prev ? [...prev, created] : [created],
      );
      invalidate(qc, projectId, roomId);
    },
  });
}

export function useUpdateArea(projectId: string, roomId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAreaRequest) => areasApi.update(projectId, roomId, id, body),
    meta: { silent: true },
    onSuccess: () => invalidate(qc, projectId, roomId),
  });
}

export function useDeleteArea(projectId: string, roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Not silent: a failed delete surfaces via the global error toast (the
    // confirm dialog has no inline error banner of its own).
    mutationFn: (id: string) => areasApi.remove(projectId, roomId, id),
    onSuccess: () => invalidate(qc, projectId, roomId),
  });
}
